/**
 * ContentAutocomplete - Autocomplete component for selecting content references
 * @module common/ContentAutocomplete
 *
 * Searches via SEARCH_CONTENT_BY_TYPES (ContentWhereInput). Supports multiple
 * content types, optional bundle/filterType, search language (prop or picker),
 * title or numeric contentId search, and optional selection preview card.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useLazyQuery } from '@apollo/client'
import {
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
  Box,
  Avatar,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Tooltip,
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import { SEARCH_CONTENT_BY_TYPES } from 'gui/components/content-detail/content-references/queries'
import type { ContentReference } from 'gui/components/content-detail/types'
import { getLanguageLabel, getLanguageChipClass } from 'services/languages'
import { LANGUAGE_EN } from 'services/languages/constants'
import { ASSET_CONTENT_REFERENCE_TYPE_D6_KEYART_NO_TEXT } from 'services/assets/content-reference-types'
import { formatContentTypeLabel } from 'gui/utils/content-type-label'
import ContentTypeChip from 'gui/components/common/ContentTypeChip'
import 'gui/components/common/_shared-styles.scss'
import './ContentAutocomplete.scss'

const SEARCH_LIMIT = 50
const DEBOUNCE_MS = 300
/** Dropdown row thumbnail (16:9-ish), matches TitleAutosuggest list density */
const OPTION_THUMB_WIDTH = 89
const OPTION_THUMB_HEIGHT = 50
/** Preview card image — larger than legacy 89×50 */
const PREVIEW_THUMB_WIDTH = 133
const PREVIEW_THUMB_HEIGHT = 75

interface ContentAutocompleteProps {
  /** Selected content references (single-select uses value[0]) */
  value: ContentReference[]
  /** Called when selection changes */
  onChange: (value: ContentReference[]) => void
  /** Called when an item is selected or cleared */
  onSelect?: (value: ContentReference | null) => void
  /** Label for the field */
  label: string
  /** Whether field is disabled */
  disabled: boolean
  /** Content types to search. Defaults to ['video']. */
  contentTypes?: string[]
  /** Search language when no language picker (no UI). Defaults to 'en'. */
  language?: string
  /** Filter by bundle in where clause */
  bundle?: string
  /** Filter by filterType in where clause */
  filterType?: string
  /** When set, renders language picker; selected language is used for the query */
  allowedLanguages?: string[]
  /** When true, shows a preview card below the field for the current selection */
  displaySelection?: boolean
  /** Key art image type for thumbnails (query variable). Default d6-keyart-no-text */
  imageType?: string
  /** Minimum characters before search runs; 0 allows immediate search and prefetch on open */
  minSearchLength?: number
}

function mapGraphqlToContentReference (
  item: {
    id: string
    contentId: number
    contentType: string
    title?: string | null
    published?: boolean | null
    titleGroupId?: string | null
    language?: string | null
    bundle?: string | null
    imageContentRelationships?: Array<{
      id: string
      type: string
      image?: { id: string; url: string } | null
    } | null> | null
  },
  imageTypeKey: string,
): ContentReference {
  const imageContentRelationships = item.imageContentRelationships || []
  const keyart = imageContentRelationships.find(
    (rel) => rel?.type === imageTypeKey && rel?.image?.url,
  )
  const thumbnailUrl = keyart?.image?.url || null

  return {
    id: item.id,
    contentId: item.contentId,
    contentType: item.contentType,
    title: item.title || '',
    published: item.published || false,
    titleGroupId: item.titleGroupId || '',
    referenceId: '',
    contentReferenceType: '',
    thumbnailUrl,
    language: item.language || undefined,
    bundle: item.bundle || undefined,
  }
}

function ContentAutocomplete (props: ContentAutocompleteProps) {
  const {
    value = [],
    onChange,
    onSelect,
    label = 'Search content',
    disabled = false,
    contentTypes,
    language: languageProp,
    bundle,
    filterType,
    allowedLanguages,
    displaySelection = false,
    imageType = ASSET_CONTENT_REFERENCE_TYPE_D6_KEYART_NO_TEXT,
    minSearchLength = 0,
  } = props

  const types = useMemo(() => contentTypes ?? ['video'], [contentTypes])
  const showLanguagePicker = Boolean(allowedLanguages && allowedLanguages.length > 0)

  const [pickerLanguage, setPickerLanguage] = useState<string>(() => {
    if (allowedLanguages && allowedLanguages.length > 0) {
      return allowedLanguages[0]
    }
    return languageProp ?? LANGUAGE_EN
  })

  useEffect(() => {
    if (allowedLanguages && allowedLanguages.length > 0) {
      if (!allowedLanguages.includes(pickerLanguage)) {
        setPickerLanguage(allowedLanguages[0])
      }
    }
  }, [allowedLanguages, pickerLanguage])

  const effectiveSearchLanguage = showLanguagePicker
    ? pickerLanguage
    : (languageProp ?? LANGUAGE_EN)

  const queryLanguage = useMemo(
    () => [effectiveSearchLanguage, '_primary'],
    [effectiveSearchLanguage],
  )

  const imageTypes = useMemo(() => [imageType], [imageType])

  const [inputValue, setInputValue] = useState<string>('')
  const [options, setOptions] = useState<ContentReference[]>(value)
  const [hasSearched, setHasSearched] = useState<boolean>(false)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [searchContent, { loading, data, error }] = useLazyQuery(SEARCH_CONTENT_BY_TYPES, {
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (error) {
      // eslint-disable-next-line no-console
      console.error('ContentAutocomplete GraphQL Error:', {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
      })
    }
  }, [error])

  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    },
    [],
  )

  const buildWhere = useCallback(
    (titlePattern: string, searchTermTrimmed: string) => {
      const where: Record<string, unknown> = {}

      if (types.length === 1) {
        where.contentType = { eq: types[0] }
      } else {
        where.contentType = { in: types }
      }

      const isNumeric = /^\d+$/.test(searchTermTrimmed)
      if (isNumeric && searchTermTrimmed.length > 0) {
        where.contentId = { eq: parseInt(searchTermTrimmed, 10) }
      } else {
        where.title = { ilike: titlePattern }
      }

      if (bundle) {
        where.bundle = { eq: bundle }
      }
      if (filterType) {
        where.filterType = { eq: filterType }
      }

      return where
    },
    [types, bundle, filterType],
  )

  const buildVariables = useCallback(
    (titlePattern: string, searchTermTrimmed: string) => ({
      where: buildWhere(titlePattern, searchTermTrimmed),
      limit: SEARCH_LIMIT,
      language: queryLanguage,
      imageTypes,
    }),
    [buildWhere, queryLanguage, imageTypes],
  )

  useEffect(() => {
    if (data?.contentList?.content) {
      const searchResults = data.contentList.content
      const optionsMap = new Map<number, ContentReference>()

      value.forEach((item) => {
        optionsMap.set(item.contentId, item)
      })

      searchResults.forEach((item: Parameters<typeof mapGraphqlToContentReference>[0] | null) => {
        if (item && !optionsMap.has(item.contentId)) {
          optionsMap.set(item.contentId, mapGraphqlToContentReference(item, imageType))
        }
      })

      setOptions(Array.from(optionsMap.values()))
    }
  }, [data, value, imageType])

  const runSearch = useCallback(
    (titlePattern: string, searchTermTrimmed: string) => {
      searchContent({ variables: buildVariables(titlePattern, searchTermTrimmed) })
    },
    [searchContent, buildVariables],
  )

  const handleOpen = useCallback(() => {
    setIsOpen(true)
    if (minSearchLength > 0) {
      return
    }
    if (!hasSearched) {
      runSearch('%', '')
      setHasSearched(true)
    }
  }, [hasSearched, minSearchLength, runSearch])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, newInputValue: string, reason: string) => {
      if (reason === 'reset') {
        setInputValue('')
        return
      }

      setInputValue(newInputValue)

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      debounceTimerRef.current = setTimeout(() => {
        const searchTerm = newInputValue.trim()
        if (minSearchLength > 0 && searchTerm.length === 0) {
          setOptions([])
          return
        }
        if (minSearchLength > 0 && searchTerm.length > 0 && searchTerm.length < minSearchLength) {
          setOptions([])
          return
        }
        const titlePattern = searchTerm ? `%${searchTerm}%` : '%'
        runSearch(titlePattern, searchTerm)
        setHasSearched(true)
      }, DEBOUNCE_MS)
    },
    [runSearch, minSearchLength],
  )

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: ContentReference | null) => {
      if (newValue) {
        onChange([newValue])
        onSelect?.(newValue)
      } else {
        onChange([])
        onSelect?.(null)
      }
    },
    [onChange, onSelect],
  )

  const getOptionLabel = useCallback((option: ContentReference) => option.title || '', [])

  const isOptionEqualToValue = useCallback(
    (option: ContentReference, val: ContentReference) => option.contentId === val.contentId,
    [],
  )

  const renderOption = useCallback(
    (optionProps: React.HTMLAttributes<HTMLLIElement>, option: ContentReference) => (
      <li {...optionProps} key={option.contentId}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: option.published ? '#4caf50' : '#f44336',
              flexShrink: 0,
            }}
          />
          <Avatar
            src={option.thumbnailUrl || undefined}
            variant="square"
            sx={{
              width: OPTION_THUMB_WIDTH,
              height: OPTION_THUMB_HEIGHT,
              borderRadius: 1,
              bgcolor: option.thumbnailUrl ? 'transparent' : '#f5f5f5',
            }}
          >
            {!option.thumbnailUrl && (
              <Box sx={{ fontSize: '10px', color: '#999', px: 0.5, textAlign: 'center' }}>
                No Image
              </Box>
            )}
          </Avatar>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexShrink: 0,
              flexWrap: 'wrap',
            }}
          >
            <ContentTypeChip contentType={option.contentType} label={option.contentType} />
            {option.language && (
              <Chip
                label={option.language}
                size="small"
                variant="outlined"
                className={`language-chip ${getLanguageChipClass(option.language)} content-autocomplete-option__language-chip`}
              />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" component="span">
              {option.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              ID: {option.contentId}
            </Typography>
          </Box>
        </Box>
      </li>
    ),
    [],
  )

  const selectedContentIds = useMemo(() => new Set(value.map((item) => item.contentId)), [value])

  const filterOptions = useCallback(
    (opts: ContentReference[]) =>
      opts.filter((option) => !selectedContentIds.has(option.contentId)),
    [selectedContentIds],
  )

  const helperText =
    isOpen && data?.contentList?.content?.length === SEARCH_LIMIT
      ? `Showing first ${SEARCH_LIMIT} results. Type more to refine your search.`
      : ''

  const noOptionsText =
    minSearchLength > 0 &&
    inputValue.trim().length > 0 &&
    inputValue.trim().length < minSearchLength
      ? `Enter at least ${minSearchLength} characters to search`
      : 'No content found'

  const selected = value[0]
  const preview = displaySelection && selected?.title

  return (
    <Box>
      {showLanguagePicker && (
        <FormControl component="fieldset" sx={{ mb: 2 }} fullWidth>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormLabel component="legend">Search Language</FormLabel>
            <Tooltip title="Select the desired language to search for content" arrow>
              <InfoIcon sx={{ fontSize: '1rem', color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Box>
          <RadioGroup
            row
            name="content-autocomplete-language"
            value={pickerLanguage}
            onChange={(e) => setPickerLanguage(e.target.value)}
          >
            {allowedLanguages!.map((lang) => (
              <FormControlLabel
                key={lang}
                value={lang}
                control={<Radio />}
                label={getLanguageLabel(lang) || lang}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}

      <Autocomplete<ContentReference, false, undefined, undefined>
        multiple={false}
        value={selected || null}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onOpen={handleOpen}
        onClose={handleClose}
        options={options}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        renderOption={renderOption}
        loading={loading}
        disabled={disabled}
        filterOptions={filterOptions}
        noOptionsText={noOptionsText}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {selected ? (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        flexShrink: 0,
                        mr: 0.75,
                        maxWidth: 'min(40%, 220px)',
                      }}
                    >
                      <ContentTypeChip
                        contentType={selected.contentType}
                        label={formatContentTypeLabel(selected)}
                        iconFontSize="0.75rem"
                        sx={{
                          height: 24,
                          maxWidth: '100%',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                          },
                        }}
                      />
                    </Box>
                  ) : null}
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {preview && (
        <Box sx={{ mt: 1, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            {selected.thumbnailUrl && (
              <Box sx={{ flexShrink: 0, borderRadius: 1, overflow: 'hidden' }}>
                <img
                  src={selected.thumbnailUrl}
                  width={PREVIEW_THUMB_WIDTH}
                  height={PREVIEW_THUMB_HEIGHT}
                  style={{ objectFit: 'cover', display: 'block' }}
                  alt=""
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 0.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {selected.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ID: {selected.contentId}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <ContentTypeChip
                  contentType={selected.contentType}
                  label={formatContentTypeLabel(selected)}
                  iconFontSize="0.875rem"
                  sx={{ fontSize: '0.75rem' }}
                />
                {selected.language && (
                  <Chip
                    label={selected.language}
                    size="small"
                    variant="outlined"
                    className={`language-chip ${getLanguageChipClass(selected.language)} content-autocomplete-preview__language-chip`}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default ContentAutocomplete
